import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import config from '../../config';
import { Loader2, Wifi, WifiOff, Clock, Navigation, Phone, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = config.API_URL;
const MAPS_KEY = config.GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];

const DRIVER_STATUSES = {
  EN_MISSION: { label: 'En mission', color: '#3B82F6', icon: Navigation },
  EN_LIGNE: { label: 'En ligne', color: '#10B981', icon: Wifi },
  HORS_LIGNE_RECENT: { label: 'Hors ligne récent', color: '#F59E0B', icon: Clock },
  DECONNECTE: { label: 'Déconnecté', color: '#6B7280', icon: WifiOff },
};

const STALE_THRESHOLD = 120;

const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

function createMarkerIcon(color) {
  return {
    path: window.google?.maps?.SymbolPath?.CIRCLE,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 2,
    scale: 9,
  };
}

export function PartnerMapTab() {
  const [drivers, setDrivers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY, libraries: LIBRARIES });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [locRes, missRes, drvRes] = await Promise.all([
        axios.get(`${API_URL}/api/partner/live-locations`, { headers }),
        axios.get(`${API_URL}/api/partner/missions`, { headers }),
        axios.get(`${API_URL}/api/partner/drivers`, { headers }),
      ]);

      const locs = locRes.data.locations || [];
      const allDrivers = drvRes.data.drivers || [];
      const now = Date.now();

      // Merge location data with driver data
      const enriched = allDrivers.map(d => {
        const loc = locs.find(l => l.driver_id === d.id);
        let driverStatus = 'DECONNECTE';
        if (loc) {
          const lastUpdate = loc.last_update ? new Date(loc.last_update).getTime() : 0;
          const stale = (now - lastUpdate) / 1000 > STALE_THRESHOLD;
          if (loc.dispatch_task_id) driverStatus = 'EN_MISSION';
          else if (!stale) driverStatus = 'EN_LIGNE';
          else driverStatus = 'HORS_LIGNE_RECENT';
        }
        return {
          ...d,
          lat: loc?.lat,
          lng: loc?.lng,
          last_update: loc?.last_update,
          driverStatus,
          hasLocation: !!loc,
        };
      });

      setDrivers(enriched);
      setMissions(missRes.data.missions || []);
    } catch {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 15000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Fit bounds to drivers with locations
    setTimeout(() => {
      const withLoc = drivers.filter(d => d.lat && d.lng);
      if (withLoc.length > 0 && window.google) {
        const bounds = new window.google.maps.LatLngBounds();
        withLoc.forEach(d => bounds.extend({ lat: d.lat, lng: d.lng }));
        map.fitBounds(bounds, 80);
      }
    }, 500);
  }, [drivers]);

  // Fit bounds when drivers change
  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    const withLoc = drivers.filter(d => d.lat && d.lng);
    if (withLoc.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      withLoc.forEach(d => bounds.extend({ lat: d.lat, lng: d.lng }));
      if (withLoc.length === 1) {
        mapRef.current.setCenter({ lat: withLoc[0].lat, lng: withLoc[0].lng });
        mapRef.current.setZoom(14);
      } else {
        mapRef.current.fitBounds(bounds, 80);
      }
    }
  }, [drivers.length]);

  const onlineCount = drivers.filter(d => d.driverStatus === 'EN_LIGNE' || d.driverStatus === 'EN_MISSION').length;
  const missionCount = drivers.filter(d => d.driverStatus === 'EN_MISSION').length;

  const FLEET_STATUS_LABELS = {
    pending_proposal: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
    driver_assigned: { label: 'Attribué', cls: 'bg-purple-500/20 text-purple-400' },
    driver_accepted: { label: 'Accepté', cls: 'bg-emerald-500/20 text-emerald-400' },
    completed: { label: 'Terminée', cls: 'bg-gray-500/20 text-gray-400' },
  };

  if (!isLoaded) return <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="partner-map-tab">
      {/* Stats bar */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-[#E5E5E5]/50">{drivers.length} chauffeurs</span>
        <span className="flex items-center gap-1 text-emerald-400"><Wifi className="w-3 h-3" />{onlineCount} en ligne</span>
        <span className="flex items-center gap-1 text-blue-400"><Navigation className="w-3 h-3" />{missionCount} en mission</span>
        <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#E5E5E5]/40 hover:text-[#C9A24D] h-7 ml-auto" data-testid="refresh-map">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        {/* Map */}
        <div className="flex-1 rounded-lg overflow-hidden border border-[#2A2A2A]" style={{ minHeight: 400 }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%', minHeight: 400 }}
            center={{ lat: 43.2965, lng: 5.3698 }}
            zoom={12}
            onLoad={onMapLoad}
            options={{
              styles: [
                { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
              ],
              disableDefaultUI: true,
              zoomControl: true,
              fullscreenControl: true,
            }}
          >
            {drivers.filter(d => d.lat && d.lng).map(d => (
              <Marker
                key={d.id}
                position={{ lat: d.lat, lng: d.lng }}
                icon={createMarkerIcon(DRIVER_STATUSES[d.driverStatus]?.color || '#6B7280')}
                onClick={() => setSelectedDriver(d)}
              />
            ))}
            {selectedDriver && selectedDriver.lat && (
              <InfoWindow
                position={{ lat: selectedDriver.lat, lng: selectedDriver.lng }}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <div className="p-1 min-w-[160px]">
                  <p className="font-semibold text-sm">{selectedDriver.first_name} {selectedDriver.last_name}</p>
                  <p className="text-xs text-gray-500">{DRIVER_STATUSES[selectedDriver.driverStatus]?.label}</p>
                  {selectedDriver.phone && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" />{selectedDriver.phone}
                    </p>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {/* Driver list sidebar */}
        <div className="w-full lg:w-64 space-y-2" data-testid="partner-driver-list">
          <p className="text-[#C9A24D] text-xs font-semibold uppercase tracking-wider">Chauffeurs</p>
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-[#C9A24D] animate-spin" /></div>
          ) : drivers.length === 0 ? (
            <p className="text-[#666] text-xs text-center py-3">Aucun chauffeur</p>
          ) : (
            <div className="space-y-1 max-h-[50vh] lg:max-h-[calc(100vh-300px)] overflow-y-auto">
              {drivers.sort((a, b) => {
                const order = { EN_MISSION: 0, EN_LIGNE: 1, HORS_LIGNE_RECENT: 2, DECONNECTE: 3 };
                return (order[a.driverStatus] ?? 9) - (order[b.driverStatus] ?? 9);
              }).map(d => {
                const st = DRIVER_STATUSES[d.driverStatus] || DRIVER_STATUSES.DECONNECTE;
                const StIcon = st.icon;
                return (
                  <button key={d.id}
                    onClick={() => {
                      setSelectedDriver(d);
                      if (d.lat && d.lng && mapRef.current) {
                        mapRef.current.panTo({ lat: d.lat, lng: d.lng });
                        mapRef.current.setZoom(15);
                      }
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                      selectedDriver?.id === d.id ? 'bg-[#C9A24D]/10 border border-[#C9A24D]/30' : 'bg-[#1A1A1A] hover:bg-[#1F1F1F] border border-transparent'
                    }`}
                    data-testid={`map-driver-${d.id}`}>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: st.color }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-xs font-medium truncate">{d.first_name} {d.last_name}</p>
                      <p className="text-[10px] flex items-center gap-1" style={{ color: st.color }}>
                        <StIcon className="w-2.5 h-2.5" />{st.label}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Recent missions summary */}
          {missions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#2A2A2A]">
              <p className="text-[#C9A24D] text-xs font-semibold uppercase tracking-wider mb-2">Missions récentes</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {missions.slice(0, 8).map(m => {
                  const st = FLEET_STATUS_LABELS[m.fleet_status] || { label: m.fleet_status, cls: 'bg-gray-500/20 text-gray-400' };
                  return (
                    <div key={m.id} className="p-2 bg-[#1A1A1A] rounded-lg" data-testid={`map-mission-${m.id}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                        <span className="text-[10px] text-white/30">{m.start_time ? new Date(m.start_time).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}</span>
                      </div>
                      <p className="text-[10px] text-white/60 truncate">{m.pickup_address || '—'}</p>
                      {m.driver_name && <p className="text-[10px] text-white/30">{m.driver_name}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
